import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { BASE_LAYERS } from '../../config/mapLayers'
import { SIDC_AXIS_OF_ADVANCE } from './tactical/sidc'

/** Per-layer display state for a base map tile layer. */
export interface BaseLayerState {
    /** Matches a `BaseLayerDefinition.id` in the layer catalog. */
    id: string
    /** Whether the layer is currently drawn on the map. */
    visible: boolean
    /** Layer opacity, 0 (transparent) .. 1 (opaque). */
    opacity: number
}

/** Which drawing tool is active inside the Edit mode submenu. */
export type EditTool = 'marker'

interface MapUiState {
    mgrsGridEnabled: boolean
    /**
     * Base layers ordered top-to-bottom: index 0 is drawn on top of the map
     * (and shown at the top of the layers panel). Reordering here changes the
     * z-stack so e.g. satellite can be blended over OSM.
     */
    baseLayers: BaseLayerState[]
    /** Active tool within Edit mode (`marker` opens the add-marker modal on click). */
    activeEditTool: EditTool
    /**
     * APP-6D SIDC of the graphic Lines mode draws. The SIDC is the discriminator
     * everywhere — the symbol registry, the backend column and the CoT payload all
     * key off it — so the UI stores the code itself rather than a local nickname.
     */
    activeLineSidc: string
    /**
     * Parent the next drawn graphic attaches to, or null for a standalone one.
     *
     * Set before drawing starts (by clicking the unit or marker in Lines mode),
     * never inferred from where the first vertex landed — snapping to the
     * nearest entity would make attachment a guess, and a line attached to the
     * wrong squad is worse than one attached to nothing.
     *
     * One value rather than two nullable ids, so "attached to both" is not
     * representable — the backend rejects that pair anyway, since there would be
     * no answer to whose faction the graphic inherits.
     */
    graphicAttachment: GraphicAttachment | null
    /**
     * Id of the line the next finished draw *replaces*, or null for a new one.
     *
     * "Edit" on a line is a redraw: the tool is the only way to express a shape,
     * so editing reuses it and PATCHes the result onto the existing row. Keeping
     * the id here (rather than making a second drawing mode) means the draw flow
     * itself is unchanged — only what happens on finish differs.
     */
    editingGraphicId: string | null
}

/** What a drawn graphic will hang off: a unit's plan, or a marker. */
export interface GraphicAttachment {
    kind: 'unit' | 'marker'
    id: string
    /**
     * Position to seed as the line's first vertex, if the attachment was made
     * from somewhere that knows one — picking "draw line" on a marker starts the
     * tail on that marker, so the user only aims the tip.
     *
     * Carried on the attachment rather than in a field of its own so the parent
     * and the point the line starts from cannot get out of step.
     */
    anchor?: { lat: number; lng: number }
    /**
     * The parent's own SIDC, when the attachment was made from somewhere that
     * knows it. The drawn graphic inherits its affiliation from this (see
     * `graphicSidcForParent`), so a line drawn from an enemy marker is an enemy
     * line without the user restating it.
     *
     * Carried on the attachment for the same reason as `anchor`: the parent and
     * the symbology derived from it cannot then get out of step.
     */
    sidc?: string
}

// Catalog order is OSM -> topo -> satellite; reversed it puts OSM at the
// bottom of the stack with the overlays above it, ready to be blended on top.
// OSM is the only layer enabled by default (preserves the previous behaviour).
const initialBaseLayers: BaseLayerState[] = [...BASE_LAYERS]
    .reverse()
    .map((def) => ({ id: def.id, visible: def.id === 'osm', opacity: 1 }))

const initialState: MapUiState = {
    mgrsGridEnabled: false,
    baseLayers: initialBaseLayers,
    activeEditTool: 'marker',
    activeLineSidc: SIDC_AXIS_OF_ADVANCE,
    graphicAttachment: null,
    editingGraphicId: null,
}

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value))

export const mapUiSlice = createSlice({
    name: 'mapUi',
    initialState,
    reducers: {
        toggleMgrsGrid: (state) => {
            state.mgrsGridEnabled = !state.mgrsGridEnabled
        },
        setActiveEditTool: (state, action: PayloadAction<EditTool>) => {
            state.activeEditTool = action.payload
        },
        setActiveLineSidc: (state, action: PayloadAction<string>) => {
            state.activeLineSidc = action.payload
        },
        /**
         * Begin an attached draw: the next graphic belongs to this unit or
         * marker. Passing the parent that is already selected detaches, so a
         * standalone line is always reachable without leaving the tool.
         */
        setGraphicAttachment: (
            state,
            action: PayloadAction<GraphicAttachment | null>
        ) => {
            const next = action.payload
            const current = state.graphicAttachment
            // Compare parent identity only: re-picking the same parent detaches,
            // whether or not an anchor came with it.
            state.graphicAttachment =
                next && current && current.kind === next.kind && current.id === next.id
                    ? null
                    : next
        },
        /** Begin (id) or end (null) a redraw of an existing line. */
        setEditingGraphic: (state, action: PayloadAction<string | null>) => {
            state.editingGraphicId = action.payload
        },
        toggleBaseLayer: (state, action: PayloadAction<string>) => {
            const layer = state.baseLayers.find((l) => l.id === action.payload)
            if (layer) {
                layer.visible = !layer.visible
            }
        },
        setBaseLayerOpacity: (
            state,
            action: PayloadAction<{ id: string; opacity: number }>
        ) => {
            const layer = state.baseLayers.find((l) => l.id === action.payload.id)
            if (layer) {
                layer.opacity = clamp01(action.payload.opacity)
            }
        },
        // Reorder via drag-and-drop: move the dragged layer (activeId) to the
        // slot occupied by the layer it was dropped on (overId).
        reorderBaseLayers: (
            state,
            action: PayloadAction<{ activeId: string; overId: string }>
        ) => {
            const { activeId, overId } = action.payload
            if (activeId === overId) return
            const from = state.baseLayers.findIndex((l) => l.id === activeId)
            const to = state.baseLayers.findIndex((l) => l.id === overId)
            if (from === -1 || to === -1) return
            const [moved] = state.baseLayers.splice(from, 1)
            state.baseLayers.splice(to, 0, moved)
        },
    },
})

export const {
    toggleMgrsGrid,
    setActiveEditTool,
    setActiveLineSidc,
    setGraphicAttachment,
    setEditingGraphic,
    toggleBaseLayer,
    setBaseLayerOpacity,
    reorderBaseLayers,
} = mapUiSlice.actions
export default mapUiSlice.reducer
